// src/pages/Plans.js
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    max_users: '',
    max_storage: '',
    max_products: '',
    max_suppliers: '',
    max_warehouses: '',
    support_level: '',
    features: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await api.get('/plans');
      setPlans(res.data);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error cargando planes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleOpenForm = plan => {
    if (plan) {
      setEditingId(plan.id);
      setFormData({
        name: plan.name,
        description: plan.description || '',
        price: plan.price,
        max_users: plan.max_users ?? '',
        max_storage: plan.max_storage ?? '',
        max_products: plan.max_products ?? '',
        max_suppliers: plan.max_suppliers ?? '',
        max_warehouses: plan.max_warehouses ?? '',
        support_level: plan.support_level || '',
        features: JSON.stringify(plan.features || {}, null, 2)
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        max_users: '',
        max_storage: '',
        max_products: '',
        max_suppliers: '',
        max_warehouses: '',
        support_level: '',
        features: ''
      });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        price: parseFloat(formData.price),
        max_users: formData.max_users ? parseInt(formData.max_users) : undefined,
        max_storage: formData.max_storage ? parseInt(formData.max_storage) : undefined,
        max_products: formData.max_products ? parseInt(formData.max_products) : undefined,
        max_suppliers: formData.max_suppliers ? parseInt(formData.max_suppliers) : undefined,
        max_warehouses: formData.max_warehouses ? parseInt(formData.max_warehouses) : undefined,
        support_level: formData.support_level || undefined,
        features: formData.features ? JSON.parse(formData.features) : undefined
      };

      if (editingId) {
        await api.put(`/plans/${editingId}`, payload);
        setSnackbar({ open: true, message: 'Plan actualizado', severity: 'success' });
      } else {
        await api.post('/plans', payload);
        setSnackbar({ open: true, message: 'Plan creado', severity: 'success' });
      }

      handleCloseForm();
      fetchPlans();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error guardando plan', severity: 'error' });
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    try {
      await api.delete(`/plans/${id}`);
      setSnackbar({ open: true, message: 'Plan eliminado', severity: 'info' });
      fetchPlans();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error eliminando plan', severity: 'error' });
    }
  };

  return (
    <Box p={3}>
      {/* Header & stats */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestión de Planes</Typography>
        <Box>
          <IconButton onClick={fetchPlans}><RefreshIcon /></IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm(null)}
          >
            Nuevo Plan
          </Button>
        </Box>
      </Box>

      {/* Stats cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Total de planes</Typography>
              <Typography variant="h5">{plans.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Planes gratuitos</Typography>
              <Typography variant="h5">
                {plans.filter(p => p.price === 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Precio promedio</Typography>
              <Typography variant="h5">
                {plans.length > 0
                  ? (plans.reduce((sum, p) => sum + parseFloat(p.price), 0) / plans.length).toFixed(2)
                  : '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Máx. Usuarios</TableCell>
                <TableCell>Soporte</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.price}</TableCell>
                  <TableCell>{p.max_users ?? '—'}</TableCell>
                  <TableCell>{p.support_level || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenForm(p)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Form dialog */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {[
              { label: 'Nombre', name: 'name' },
              { label: 'Descripción', name: 'description' },
              { label: 'Precio', name: 'price', type: 'number' },
              { label: 'Máx. Usuarios', name: 'max_users', type: 'number' },
              { label: 'Máx. Almacenamiento (MB)', name: 'max_storage', type: 'number' },
              { label: 'Máx. Productos', name: 'max_products', type: 'number' },
              { label: 'Máx. Proveedores', name: 'max_suppliers', type: 'number' },
              { label: 'Máx. Bodegas', name: 'max_warehouses', type: 'number' },
              { label: 'Nivel Soporte', name: 'support_level' }
            ].map(field => (
              <Grid item xs={12} sm={6} key={field.name}>
                <TextField
                  fullWidth
                  label={field.label}
                  name={field.name}
                  type={field.type || 'text'}
                  value={formData[field.name]}
                  onChange={handleChange}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (JSON)"
                name="features"
                multiline
                minRows={4}
                value={formData.features}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingId ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
